import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../Services/data/data.service";
import { Grid2, Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { ExternalApiFilterTypes, externalApiTypes, externalApiEnumBehaviorTypes } from "../../../Config/constants";
import { externalApiTableConfig } from "../../../Config/tableConfig";
import { Plus, Search, X } from "lucide-react";
import ResetFiltersButton from "../../Filter/ResetFiltersButton";
import { useNavigate } from "react-router-dom";
import Button from "../../Shared/Buttons/Button";
import HeaderBar from "../../Shared/Header/HeaderBar";
import HeaderTitle from "../../Shared/Header/HeaderTitle";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import TableExternalApi from "./Components/TableExternalApi";
import TablePagination from "@mui/material/TablePagination";
import useFilterPersistence from '../../../hooks/useFilterPersistence';

function ExternalApiOverviewPage(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [firstLoad, setFirstLoad] = useState(0);
  const GetUserId = props.Login.userid;
  const classes = useStyles();
  const [filterOn, setFilterOn] = useState(false);
  const [externalApis, setExternalApis] = useState([]);
  const [originalExternalApis, setOriginalExternalApis] = useState([]);
  const [sortColumn, setSortColumn] = useState(externalApiTableConfig.defaultSort.key);
  const [sortOrder, setSortOrder] = useState(externalApiTableConfig.defaultSort.order);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Persistent filter state ────────────────────────────────────────────────
  const {
    filters,
    setFilterText,
    setFilter,
    resetFilters,
    getFilterValue,
    hasActiveFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
  } = useFilterPersistence({
    pageKey: 'external-api-overview',
    filterSchema: {
      name:     ExternalApiFilterTypes.NAME,
      company:  ExternalApiFilterTypes.COMPANY,
      apitype:  ExternalApiFilterTypes.APITYPE,
      behavior: ExternalApiFilterTypes.BEHAVIOR,
    },
    defaultFilters: [],
    defaultPage: 0,
    defaultPageSize: 25,
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dataService.getApis(GetUserId),
      dataService.getAllCompaniesByUserId(GetUserId)
    ]).then(([apisResult, companiesResult]) => {
      // Sort the result by name in ascending order
      apisResult.sort((a, b) => a.name.localeCompare(b.name));
      setOriginalExternalApis(apisResult);
      setCompanies(companiesResult || []);
    }).finally(() => {
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply filters whenever the source data or active filters change.
  // This ensures URL-restored filters are applied after data loads.
  useEffect(() => {
    filterApi(filters);
  }, [originalExternalApis, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  if (firstLoad === 0) {
    setFirstLoad(1);
  }

  const handleSorting = (column) => {
    let newOrder = "ASC";
    let newColumn = column;
    
    if (sortColumn === column) {
      // If clicking the same column, cycle through: ASC -> DESC -> no sort
      if (sortOrder === "ASC") {
        newOrder = "DESC";
      } else {
        // Clear sorting - return to original order
        newColumn = "";
        newOrder = "ASC";
        setSortColumn("");
        setSortOrder("ASC");
        setExternalApis([...originalExternalApis]);
        return;
      }
    }
    
    setSortColumn(newColumn);
    setSortOrder(newOrder);
    
    const colConfig = externalApiTableConfig.columns.find((c) => c.key === column);

    let tempRows = [...externalApis];
    tempRows.sort((a, b) => {
      let aVal, bVal;
      if (colConfig?.getValue) {
        aVal = colConfig.getValue(a);
        bVal = colConfig.getValue(b);
      } else if (colConfig?.valueMap) {
        aVal = colConfig.valueMap[a[column]] || "";
        bVal = colConfig.valueMap[b[column]] || "";
      } else {
        aVal = a[column];
        bVal = b[column];
      }
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal > bVal) return newOrder === "ASC" ? 1 : -1;
      if (aVal < bVal) return newOrder === "ASC" ? -1 : 1;
      return 0;
    });
    setExternalApis(tempRows);
  };

  const toggleFilter = () => {
    if (filterOn) {
      resetFilters();
    }
    setFilterOn(!filterOn);
  };

  const updateFilters = (type, event) => {
    setFilterText(type, event.target.value);
    filterApi([{ type, filterValue: event.target.value }, ...filters.filter((f) => f.type !== type)]);
  };

  const updateCompanyFilter = (selectedCompany) => {
    const value = selectedCompany ? String(selectedCompany.id) : '';
    setFilter(ExternalApiFilterTypes.COMPANY, value);
    filterApi(
      value
        ? [{ type: ExternalApiFilterTypes.COMPANY, filterValue: value }, ...filters.filter((f) => f.type !== ExternalApiFilterTypes.COMPANY)]
        : filters.filter((f) => f.type !== ExternalApiFilterTypes.COMPANY)
    );
  };

  const filterApi = (thisFilters) => {
    let varFilteredApi = [...originalExternalApis];
    var i;
    for (i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case ExternalApiFilterTypes.NAME:
          varFilteredApi = varFilteredApi.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case ExternalApiFilterTypes.COMPANY:
          varFilteredApi = varFilteredApi.filter((x) => x.companyId === parseInt(filter.filterValue) || filter.filterValue === "");
          break;
        case ExternalApiFilterTypes.APITYPE:
          varFilteredApi = varFilteredApi.filter((x) => x.externalApiTypeId === parseInt(filter.filterValue));
          break;
        case ExternalApiFilterTypes.BEHAVIOR:
          if (filter.filterValue === "none") {
            varFilteredApi = varFilteredApi.filter((x) => !x.externalApiEnumBehaviorTypeId);
          } else {
            varFilteredApi = varFilteredApi.filter((x) => x.externalApiEnumBehaviorTypeId === parseInt(filter.filterValue));
          }
          break;
        default:
          break;
      }
    }
    setExternalApis(varFilteredApi);
  };

  const navigateTo = (destination) => {
    navigate(destination, { state: { from: "/externalapioverview" } });
  };

  // Utility function to get selected company
  const getSelectedCompany = () => {
    const companyId = getFilterValue(ExternalApiFilterTypes.COMPANY);
    return companies.find((company) => company.id === parseInt(companyId)) || null;
  };

  // Utility function to get search value
  const getSearchValue = () => {
    return getFilterValue(ExternalApiFilterTypes.NAME);
  };

  const updateSelectFilter = (type, value) => {
    // Immediate sync for dropdown selections
    setFilter(type, value || '');
    const withNew = value
      ? [{ type, filterValue: value }, ...filters.filter((f) => f.type !== type)]
      : filters.filter((f) => f.type !== type);
    filterApi(withNew);
  };

  const apiTypeOptions = [
    { id: externalApiTypes.DATA, name: t('apiOverview.typeData') },
    { id: externalApiTypes.STOPWATCH, name: t('apiOverview.typeStopwatch') },
    { id: externalApiTypes.NUMERIC, name: t('apiOverview.typeNumeric') },
    { id: externalApiTypes.ENUM, name: t('apiOverview.typeEnum') },
  ];

  const behaviorOptions = [
    { id: "none", name: t('apiOverview.behaviorNone') },
    { id: externalApiEnumBehaviorTypes.ANCHOR, name: t('apiOverview.behaviorAnchor') },
    { id: externalApiEnumBehaviorTypes.STICKY, name: t('apiOverview.behaviorSticky') },
  ];

  // Pagination handlers (delegated to the persistence hook)
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };
  // Calculate paginated rows
  const paginatedRows = externalApis.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <React.Fragment>
      <Box className="page-container">
        <HeaderBar>
          <HeaderTitle>{t('apiOverview.title')}</HeaderTitle>
        </HeaderBar>
        <div className={classes.tableManagementContainer}>
          <div className={classes.tableManagementContainer__start}>
            <div className={classes.searchContainer}>
              <input
                placeholder={t('apiOverview.searchPlaceholder')}
                className={classes.search__input}
                type="text"
                value={getFilterValue(ExternalApiFilterTypes.NAME)}
                onChange={(e) => updateFilters(ExternalApiFilterTypes.NAME, e)}
              />
              <Search size={16} className={classes.inputIcon} />
            </div>
            <div className={classes.filterContainer}>
              <Select value={getSelectedCompany()?.id} onChange={updateCompanyFilter} size="small">
                <SelectTrigger>{getSelectedCompany()?.name || t('apiOverview.allCompanies')}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>{t('apiOverview.allCompanies')}</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={classes.filterContainer}>
              <Select value={getFilterValue(ExternalApiFilterTypes.APITYPE)} onChange={(val) => updateSelectFilter(ExternalApiFilterTypes.APITYPE, val?.id || null)} size="small">
                <SelectTrigger>{apiTypeOptions.find((opt) => opt.id === parseInt(getFilterValue(ExternalApiFilterTypes.APITYPE)))?.name || t('apiOverview.allTypes')}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>{t('apiOverview.allTypes')}</SelectItem>
                  {apiTypeOptions.map((type) => (
                    <SelectItem key={type.id} value={type}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={classes.filterContainer}>
              <Select value={getFilterValue(ExternalApiFilterTypes.BEHAVIOR)} onChange={(val) => updateSelectFilter(ExternalApiFilterTypes.BEHAVIOR, val?.id || null)} size="small">
                <SelectTrigger>{behaviorOptions.find((b) => String(b.id) === String(getFilterValue(ExternalApiFilterTypes.BEHAVIOR)))?.name || t('apiOverview.allBehaviors')}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>{t('apiOverview.allBehaviors')}</SelectItem>
                  {behaviorOptions.map((behavior) => (
                    <SelectItem key={behavior.id} value={behavior}>
                      {behavior.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Reset filters — visible whenever any filter is active */}
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
          </div>
          <div className={classes.tableManagementContainer__end}>
            <Button size="small" startIcon={<Plus />} onClick={() => navigateTo("/createapi")}>
              {t('apiOverview.createApi')}
            </Button>
          </div>
        </div>
        <div className={classes.container}>
          <Grid2 container columnSpacing={3} rowSpacing={1} alignItems="center">
            <Grid2 item size={12}>
              <TableExternalApi
                {...props}
                toggleFilter={toggleFilter}
                filters={filters}
                handleSorting={handleSorting}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                filterOn={filterOn}
                filterApi={filterApi}
                updateFilters={updateFilters}
                rows={paginatedRows}
                searchValue={getSearchValue()}
                loading={loading}
              />
              <TablePagination
                component="div"
                count={externalApis.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={t('table.rowsPerPage')}
              />
            </Grid2>
          </Grid2>
        </div>
      </Box>
    </React.Fragment>
  );
}

export default ExternalApiOverviewPage;

const useStyles = makeStyles({
  container: {
    boxSizing: "border-box",
    maxWidth: "100%",
    marginBottom: "48px",
  },

  header: {
    width: "100%",
    height: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    marginBottom: "16px",
  },



  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
    marginRight: "16px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },


});